// src/components/ToastNotification.js
import React, { useState, useEffect, createContext, useContext, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

// Toast Context
const ToastContext = createContext();

// Hook to use toast notifications
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Individual Toast Component
const Toast = ({ id, type, title, message, duration, onRemove }) => {
  const [isExiting, setIsExiting] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => onRemove(id), 300); // Wait for exit animation
    }, duration);
    
    return () => clearTimeout(timer);
  }, [id, duration, onRemove]);
  
  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onRemove(id), 300);
  };
  
  // Icon and color configuration
  const config = {
    success: {
      icon: CheckCircle,
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      iconColor: 'text-green-600',
      textColor: 'text-green-900'
    },
    error: {
      icon: AlertCircle,
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      iconColor: 'text-red-600',
      textColor: 'text-red-900'
    },
    warning: {
      icon: AlertTriangle,
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      iconColor: 'text-yellow-600',
      textColor: 'text-yellow-900'
    },
    info: {
      icon: Info,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      iconColor: 'text-blue-600',
      textColor: 'text-blue-900'
    }
  };
  
  const { icon: Icon, bgColor, borderColor, iconColor, textColor } = config[type] || config.info;
  
  return (
    <div
      className={`flex items-start gap-3 w-full max-w-sm ${bgColor} ${borderColor} border rounded-lg shadow-lg p-4 transition-all duration-300 ${
        isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'
      }`}
    >
      <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />
      <div className="flex-1">
        {title && (
          <h4 className={`font-medium ${textColor} mb-1`}>{title}</h4>
        )}
        {message && (
          <p className={`text-sm ${textColor} opacity-90`}>{message}</p>
        )}
      </div>
      <button
        onClick={handleClose}
        className={`${textColor} opacity-60 hover:opacity-100 transition-opacity`}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Toast Container Component
const ToastContainer = ({ toasts, removeToast }) => {
  const containerNode = (
    <div className="fixed top-20 right-4 z-[100] space-y-3">
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          {...toast}
          onRemove={removeToast}
        />
      ))}
    </div>
  );

  // Portal-mount at document.body to escape any transform-ancestor in the
  // React tree that would break position: fixed containment.
  return createPortal(containerNode, document.body);
};

// Toast Provider Component
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((type, title, message, duration = 5000) => {
    // Use crypto.randomUUID for collision-free IDs. Date.now() risked collisions
    // when multiple toasts fired in the same millisecond. See DEF-034.
    const id = crypto.randomUUID();
    setToasts(prev => [...prev, { id, type, title, message, duration }]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Convenience methods — wrapped in useMemo so the returned reference is
  // stable across renders. Without this, every consumer that includes `toast`
  // in a useMemo/useCallback/useEffect dep array would invalidate on every
  // ToastProvider render. See DEF-008 fix history for context.
  const toast = useMemo(() => ({
    success: (title, message, duration) => addToast('success', title, message, duration),
    error: (title, message, duration) => addToast('error', title, message, duration),
    warning: (title, message, duration) => addToast('warning', title, message, duration),
    info: (title, message, duration) => addToast('info', title, message, duration),
    remove: removeToast,
  }), [addToast, removeToast]);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

// Example usage in components:
/*
import { useToast } from '../components/ToastNotification';

const MyComponent = () => {
  const toast = useToast();
  
  const handleSave = () => {
    // Do save logic
    toast.success('Success!', 'Your changes have been saved.');
  };
  
  const handleError = () => {
    toast.error('Error', 'Something went wrong. Please try again.');
  };
  
  const handleInfo = () => {
    toast.info('Info', 'New features are available!');
  };
  
  const handleWarning = () => {
    toast.warning('Warning', 'Your session will expire in 5 minutes.');
  };
};
*/

export default ToastProvider;